import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/authService';
import { useTheme } from '../../store/themeStore';
import { useExportUiStore } from '../../store/exportUiStore';
import ExportLoadingOverlay from '../../components/shared/ExportLoadingOverlay';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { BsBoxArrowRight, BsMoonFill, BsPerson, BsSunFill } from 'react-icons/bs';
import {
  LayoutDashboard,
  Landmark,
  Store,
  FileText,
  DollarSign,
  GitBranch,
  ListChecks,
  Gauge,
  User,
  Users,
  Code,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Link as LinkIcon,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  Settings,
  KeyRound,
  ClipboardList,
} from 'lucide-react';
import { AppearanceSettingsDrawer } from '../../components/settings/AppearanceSettingsDrawer';
import { useAppearanceStore } from '../../store/appearanceStore';
import type { LucideIcon } from 'lucide-react';
import { toCapitalizedWords } from '../../utils/helpers';
import brandLogo from '../../assets/logo.png';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  path?: string;
  label: string;
  icon: LucideIcon;
  moduleCode?: string;
  alwaysVisible?: boolean;
  expandable?: boolean;
  id?: string;
  children?: NavItem[];
  hasSubmenu?: boolean;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const navLinkClass = ({ isActive }: { isActive: boolean }, extra = '') =>
  `nav-item ${extra} ${isActive ? 'active' : ''}`.trim();

const submenuLinkClass = ({ isActive }: { isActive: boolean }) =>
  `nav-item submenu-item ${isActive ? 'active' : ''}`;

function getUserInitials(name?: string): string {
  if (!name?.trim()) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}

function formatUserRole(user: { adminType?: string; role?: string } | null): string {
  if (!user) return 'Admin';
  const titled = toCapitalizedWords(user.adminType || user.role, 'Admin');
  return titled.charAt(0) + titled.slice(1).toLowerCase();
}

function pathMatches(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function findBreadcrumb(sections: NavSection[], pathname: string) {
  for (const section of sections) {
    for (const item of section.items) {
      if (item.path && pathMatches(pathname, item.path)) {
        return { section: section.title, label: item.label };
      }
      for (const child of item.children ?? []) {
        if (child.path && pathMatches(pathname, child.path)) {
          return { section: section.title, label: child.label };
        }
      }
    }
  }
  return null;
}

function itemHasActiveChild(item: NavItem, pathname: string): boolean {
  return (item.children ?? []).some(child => !!child.path && pathMatches(pathname, child.path));
}

const ICON_STROKE = 1.75;

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const exportingExcel = useExportUiStore((state) => state.exporting);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<string[]>(() => {
    const initial = ['dashboards'];
    // Open the Risk management group on first paint when landing on one of its pages, so the
    // active child is visible instead of hidden inside a collapsed parent.
    if (
      pathMatches(location.pathname, '/dashboard/fraud') ||
      pathMatches(location.pathname, '/dashboard/risk-rules')
    ) {
      initial.push('risk');
    }
    return initial;
  });
  const sidebarType = useAppearanceStore((s) => s.settings.sidebarType);
  const setSidebarType = useAppearanceStore((s) => s.setSidebarType);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    () => sidebarType === 'mini' || localStorage.getItem('sidebar-collapsed') === 'true'
  );
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [headerAccountOpen, setHeaderAccountOpen] = useState(false);

  useEffect(() => {
    setIsCollapsed(sidebarType === 'mini');
    localStorage.setItem('sidebar-collapsed', String(sidebarType === 'mini'));
  }, [sidebarType]);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      setSidebarType(next ? 'mini' : 'full');
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  const sidebarToggleLabel = isCollapsed ? 'Expand sidebar' : 'Collapse sidebar';

  const closeAccountMenus = () => {
    setProfileMenuOpen(false);
    setHeaderAccountOpen(false);
  };

  const handleLogout = async () => {
    closeAccountMenus();
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      logout();
      navigate('/login');
    }
  };

  const handleProfileMenuNavigate = (path: string) => {
    closeAccountMenus();
    navigate(path);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const allMenuSections: NavSection[] = [
    {
      title: 'Analytics',
      items: [
        { path: '/dashboard/transaction-dashboard', label: 'Transaction Analytics', icon: LayoutDashboard, moduleCode: 'TRAN-MON-09' },
      ],
    },
    {
      title: 'Operations',
      items: [
        { path: '/dashboard/acquirers', label: 'Acquirers', icon: Landmark },
        { path: '/dashboard/merchants/manage', label: 'Merchants', icon: Store },
        { path: '/dashboard/terminals', label: 'Terminals', icon: FileText },
        { path: '/dashboard/dcc-markup-rates', label: 'DCC Markup Rates', icon: DollarSign },
        { path: '/dashboard/payment-routing', label: 'Payment Routing', icon: GitBranch },
        { path: '/dashboard/error-codes-mapping', label: 'Error Codes Mapping', icon: Code },
        {
          label: 'Risk Management',
          icon: ShieldAlert,
          expandable: true,
          id: 'risk',
          children: [
            { path: '/dashboard/fraud', label: 'Risk Screening', icon: ShieldCheck },
            { path: '/dashboard/risk-rules', label: 'Rules', icon: SlidersHorizontal },
          ],
        },
        { path: '/dashboard/storefront-url-review', label: 'URL Approvals', icon: ListChecks },
      ],
    },
    {
      title: 'Support',
      items: [
        { path: '/dashboard/transaction-monitoring', label: 'Transaction Monitoring', icon: Gauge, moduleCode: 'TRAN-MON-09' },
        { path: '/dashboard/logs', label: 'Audit Logs', icon: ClipboardList },
      ],
    },
    {
      title: 'Users',
      items: [
        { path: '/dashboard/profile', label: 'My Profile', icon: User, moduleCode: 'PROFILE' },
        { path: '/dashboard/user-management', label: 'User Management', icon: Users },
        { path: '/dashboard/permissions', label: 'Permissions', icon: KeyRound },
      ],
    },
    {
      title: 'Developers',
      items: [
        // Gated by the per-user permission matrix (merchant modules): revoking a module's View hides
        // the link here AND blocks the route (see RequireModule + MERCHANT_ALLOWED_MODULES). Seeded
        // on by default for every merchant, so normal merchants still see all three.
        { path: '/dashboard/developers', label: 'Developers', icon: Code, moduleCode: 'MERCH-DEVELOPERS' },
        { path: '/dashboard/documentation', label: 'Documentation', icon: FileText, moduleCode: 'MERCH-DOCS' },
        { path: '/dashboard/storefront-urls', label: 'URLs', icon: LinkIcon, moduleCode: 'MERCH-STOREFRONT-URLS' },
      ],
    },
  ];

  // Expand a group only while one of its pages is open, and collapse it again on any other page.
  // The parent used to stay open (and look selected) after leaving Risk management.
  useEffect(() => {
    const expandable = allMenuSections.flatMap(section =>
      section.items.filter((item): item is NavItem & { id: string } => Boolean(item.expandable && item.id))
    );
    setExpandedSections(prev => {
      let next = prev;
      for (const item of expandable) {
        const active = itemHasActiveChild(item, location.pathname);
        const open = next.includes(item.id);
        if (active && !open) next = [...next, item.id];
        else if (!active && open) next = next.filter(id => id !== item.id);
      }
      return next;
    });
  }, [location.pathname]);

  // Merchants only see the pages their role has been granted view access to (Transaction
  // Monitoring + Profile), plus any item explicitly marked alwaysVisible. A SUPER_ADMIN sees the
  // full menu EXCEPT the Developers section. Every plain ADMIN (never SUPER_ADMIN) is a referral
  // admin — not an opt-in flag, just what AdminType.ADMIN means — and gets a third, hardcoded
  // subset instead of the merchant branch's moduleAccesses-based filtering (a referral admin has
  // no per-module Role grants of their own, they use the plain seeded "ADMIN" role): Transaction
  // Analytics + Transaction Monitoring + My Profile only, mirroring what a fully-granted merchant
  // would see minus Developers.
  const isMerchant = user?.userType === 'MERCHANT';
  const isReferralAdmin = !isMerchant && String(user?.adminType ?? '').trim().toUpperCase() === 'ADMIN';
  const isSuperAdmin = !isMerchant && String(user?.adminType ?? user?.role ?? '').trim().toUpperCase() === 'SUPER_ADMIN';
  const viewableModuleCodes = new Set(
    (user?.moduleAccesses ?? []).filter(access => access.canView).map(access => access.moduleCode)
  );
  // A regular admin (not super, not referral) with a configured per-user matrix is gated on the nav
  // items that CARRY a moduleCode; items without one stay visible (not yet mapped), and an admin with
  // no matrix at all keeps the full menu — so gating tightens incrementally and never locks anyone out.
  const adminMatrixConfigured = !isMerchant && !isSuperAdmin && !isReferralAdmin
    && (user?.moduleAccesses?.length ?? 0) > 0;
  const REFERRAL_ADMIN_VISIBLE_PATHS = new Set([
    '/dashboard/transaction-dashboard',
    '/dashboard/transaction-monitoring',
    '/dashboard/profile',
    '/dashboard/documentation',
  ]);
  const menuSections = isMerchant
    ? allMenuSections
        .map(section => ({
          ...section,
          items: section.items.filter(
            (item: NavItem) => item.alwaysVisible || (item.moduleCode && viewableModuleCodes.has(item.moduleCode))
          ),
        }))
        .filter(section => section.items.length > 0)
    : isReferralAdmin
      ? allMenuSections
          .map(section => ({
            ...section,
            items: section.items.filter((item: NavItem) => item.path && REFERRAL_ADMIN_VISIBLE_PATHS.has(item.path)),
          }))
          .filter(section => section.items.length > 0)
      : adminMatrixConfigured
        ? allMenuSections
            .filter(section => section.title !== 'Developers')
            .map(section => ({
              ...section,
              items: section.items.filter(
                (item: NavItem) =>
                  item.alwaysVisible || !item.moduleCode || viewableModuleCodes.has(item.moduleCode)
              ),
            }))
            .filter(section => section.items.length > 0)
        : allMenuSections.filter(section => section.title !== 'Developers');

  const userInitials = getUserInitials(user?.fullName);
  const displayName = toCapitalizedWords(user?.fullName, 'User');
  const userRole = formatUserRole(user);
  const canViewProfile = !isMerchant || viewableModuleCodes.has('PROFILE');
  // const workspaceLabel = import.meta.env.DEV ? 'Development workspace' : 'Production workspace';

  const renderNavIcon = (Icon: LucideIcon, tooltipLabel?: string) => {
    const icon = (
      <span className="nav-icon">
        <Icon strokeWidth={ICON_STROKE} />
      </span>
    );
    if (!isCollapsed || !tooltipLabel) return icon;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{icon}</TooltipTrigger>
        <TooltipContent side="right">{tooltipLabel}</TooltipContent>
      </Tooltip>
    );
  };

  const breadcrumbMatch = findBreadcrumb(allMenuSections, location.pathname);
  const moduleConsolePrefixes = [
    '/dashboard/transaction-dashboard',
    '/dashboard/profile',
    '/dashboard/transaction-monitoring',
    '/dashboard/acquirers',
    '/dashboard/merchants',
    '/dashboard/terminals',
    '/dashboard/dcc-markup-rates',
    '/dashboard/payment-routing',
    '/dashboard/error-codes-mapping',
    '/dashboard/fraud',
    '/dashboard/risk-rules',
    '/dashboard/storefront-url-review',
    '/dashboard/logs',
    '/dashboard/user-management',
    '/dashboard/permissions',
    '/dashboard/developers',
    '/dashboard/documentation',
    '/dashboard/storefront-urls',
  ];
  const isModuleConsolePage = moduleConsolePrefixes.some((prefix) =>
    location.pathname.includes(prefix),
  );
  const breadcrumb = breadcrumbMatch;

  const renderAccountMenu = (side: 'top' | 'bottom', align: 'start' | 'end') => (
    <PopoverContent className="dh-profile-menu" align={align} side={side} sideOffset={8}>
      <div className="dh-profile-menu-hero">
        <div className="dh-profile-menu-hero-glow" aria-hidden />
        <div className="dh-profile-menu-identity">
          <span className="dh-profile-menu-avatar">{userInitials}</span>
          <div className="dh-profile-menu-meta">
            <div className="dh-profile-menu-name">{displayName}</div>
            <div className="dh-profile-menu-email">{user?.email || '—'}</div>
            <span className="dh-profile-menu-role">{userRole}</span>
          </div>
        </div>
      </div>
      <div className="dh-profile-menu-body">
        {canViewProfile && (
          <div className="dh-profile-menu-list">
            <button
              type="button"
              className="dh-profile-menu-link"
              onClick={() => handleProfileMenuNavigate('/dashboard/profile')}
            >
              <span className="dh-profile-menu-link-icon-wrap">
                <BsPerson className="dh-profile-menu-link-icon" />
              </span>
              <span className="dh-profile-menu-link-text">
                <span className="dh-profile-menu-link-label">My Profile</span>
                <span className="dh-profile-menu-link-hint">Account Settings</span>
              </span>
            </button>
          </div>
        )}
        <div className="dh-profile-menu-theme" role="group" aria-label="Color theme">
          <button
            type="button"
            className={theme === 'light' ? 'is-active' : ''}
            onClick={() => theme !== 'light' && toggleTheme()}
          >
            <BsSunFill aria-hidden />
            Light
          </button>
          <button
            type="button"
            className={theme === 'dark' ? 'is-active' : ''}
            onClick={() => theme !== 'dark' && toggleTheme()}
          >
            <BsMoonFill aria-hidden />
            Dark
          </button>
        </div>
        <button
          type="button"
          className="dh-profile-menu-link dh-profile-menu-signout"
          onClick={handleLogout}
        >
          <span className="dh-profile-menu-link-icon-wrap">
            <BsBoxArrowRight className="dh-profile-menu-link-icon" />
          </span>
          <span className="dh-profile-menu-link-text">
            <span className="dh-profile-menu-link-label">Sign Out</span>
            <span className="dh-profile-menu-link-hint">End This Session</span>
          </span>
        </button>
      </div>
    </PopoverContent>
  );

  return (
    <div
      className="dashboard-layout"
      // Blocks the browser's native right-click menu (which includes "Inspect") across the whole
      // dashboard shell for a referral admin — a UI-level deterrent only, not real protection:
      // DevTools opened via keyboard shortcut (F12 / Cmd+Opt+I) or the browser menu bar is
      // unaffected. Was previously only wired up on the Transaction Monitoring page itself (see
      // its own onContextMenu) — moved up here so it covers every page a referral admin can reach
      // (Transaction Analytics, Profile, Documentation), not just that one table.
      onContextMenu={(e) => {
        if (isReferralAdmin) e.preventDefault();
      }}
    >
      <div className="dashboard-shell-top">
        <div className="dashboard-brand">
          <img src={brandLogo} alt="money eu" className="brand-wordmark" />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="sidebar-collapse-btn"
                onClick={toggleSidebar}
                aria-label={sidebarToggleLabel}
                aria-expanded={!isCollapsed}
              >
                {isCollapsed ? (
                  <PanelLeft strokeWidth={ICON_STROKE} size={16} aria-hidden />
                ) : (
                  <PanelLeftClose strokeWidth={ICON_STROKE} size={16} aria-hidden />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{sidebarToggleLabel}</TooltipContent>
          </Tooltip>
        </div>

        <header
          className={
            isModuleConsolePage
              ? 'dashboard-header dashboard-header--tm-compact'
              : 'dashboard-header'
          }
        >
          <div className="header-left">
            {breadcrumb ? (
              <nav className="dh-breadcrumb" aria-label="Breadcrumb">
                <span>{breadcrumb.section}</span>
                <span className="dh-breadcrumb-sep" aria-hidden>/</span>
                <span className="dh-breadcrumb-current">{breadcrumb.label}</span>
              </nav>
            ) : null}
          </div>
          <div className="header-right">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="dh-theme-btn"
                  onClick={() => setAppearanceOpen(true)}
                  aria-label="Appearance settings"
                >
                  <Settings size={16} strokeWidth={2} aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Appearance settings</TooltipContent>
            </Tooltip>
            <AppearanceSettingsDrawer open={appearanceOpen} onOpenChange={setAppearanceOpen} />
            <div className="dh-theme-toggle" role="group" aria-label="Color theme">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={`dh-theme-btn${theme === 'light' ? ' active' : ''}`}
                    onClick={() => theme !== 'light' && toggleTheme()}
                    aria-label="Switch to light mode"
                    aria-pressed={theme === 'light'}
                  >
                    <BsSunFill aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {theme === 'light' ? 'Light mode active' : 'Switch to light mode'}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={`dh-theme-btn${theme === 'dark' ? ' active' : ''}`}
                    onClick={() => theme !== 'dark' && toggleTheme()}
                    aria-label="Switch to dark mode"
                    aria-pressed={theme === 'dark'}
                  >
                    <BsMoonFill aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {theme === 'dark' ? 'Dark mode active' : 'Switch to dark mode'}
                </TooltipContent>
              </Tooltip>
            </div>
            <Popover open={headerAccountOpen} onOpenChange={setHeaderAccountOpen}>
              <PopoverTrigger asChild>
                <button type="button" className="dh-header-avatar-btn" aria-label="Account menu">
                  {userInitials}
                </button>
              </PopoverTrigger>
              {renderAccountMenu('bottom', 'end')}
            </Popover>
          </div>
        </header>
      </div>

      <div className="dashboard-shell-body">
      {/* Sidebar nav — width collapses; brand row above stays full width */}
      <aside className={`dashboard-sidebar${isCollapsed ? ' collapsed' : ''}`}>
        {/* {!isCollapsed && (
          <div className="sidebar-workspace">
            <span className="sidebar-workspace-dot" aria-hidden />
            <span className="sidebar-workspace-label">{workspaceLabel}</span>
          </div>
        )} */}

        <nav className="dashboard-nav">
          {menuSections.map((section, sectionIdx) => (
            <div
              key={sectionIdx}
              className="nav-section"
            >
              {!isCollapsed && section.title ? (
                <div className="section-title">{section.title}</div>
              ) : null}
              {section.items.map((item: NavItem, itemIdx) => {
                const groupOpen = expandedSections.includes(item.id || '');
                const groupActive = item.expandable && itemHasActiveChild(item, location.pathname);
                return (
                  <div key={itemIdx}>
                    {item.expandable ? (
                      <>
                        <button
                          type="button"
                          className={`nav-item expandable${groupActive ? ' active' : ''}`}
                          onClick={() => item.id && toggleSection(item.id)}
                          aria-label={isCollapsed ? item.label : undefined}
                        >
                          {renderNavIcon(item.icon, item.label)}
                          {!isCollapsed && <span className="nav-label">{item.label}</span>}
                          {!isCollapsed && (
                            <span className={`expand-icon ${groupOpen ? 'expanded' : ''}`}>
                              <ChevronRight strokeWidth={ICON_STROKE} />
                            </span>
                          )}
                        </button>
                        {groupOpen && (
                          <div className="nav-submenu">
                            {item.children?.map((child, childIdx) => (
                              <NavLink
                                key={childIdx}
                                to={child.path || '#'}
                                end={child.path === '/dashboard'}
                                className={submenuLinkClass}
                              >
                                {renderNavIcon(child.icon)}
                                {!isCollapsed && <span className="nav-label">{child.label}</span>}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <NavLink
                        to={item.path || '#'}
                        end={item.path === '/dashboard'}
                        className={({ isActive }) => navLinkClass({ isActive })}
                        aria-label={isCollapsed ? item.label : undefined}
                      >
                        {renderNavIcon(item.icon, item.label)}
                        {!isCollapsed && <span className="nav-label">{item.label}</span>}
                        {!isCollapsed && item.hasSubmenu && (
                          <span className="submenu-arrow">
                            <ChevronRight strokeWidth={ICON_STROKE} />
                          </span>
                        )}
                      </NavLink>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Popover open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="user-profile"
                aria-label="Account menu"
              >
                {isCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="user-avatar-wrap">
                        <span className="user-avatar">{userInitials}</span>
                        <span className="user-status-dot" aria-hidden />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right">Account menu</TooltipContent>
                  </Tooltip>
                ) : (
                  <span className="user-avatar-wrap">
                    <span className="user-avatar">{userInitials}</span>
                    <span className="user-status-dot" aria-hidden />
                  </span>
                )}
                {!isCollapsed && (
                  <span className="user-details">
                    <span className="user-name">{displayName}</span>
                    <span className="user-role">{userRole}</span>
                  </span>
                )}
                {!isCollapsed && (
                  <ChevronDown className="user-chevron" strokeWidth={ICON_STROKE} />
                )}
              </button>
            </PopoverTrigger>
            {renderAccountMenu('top', 'start')}
          </Popover>
        </div>
      </aside>

      <div className="dashboard-main">
        <main className="dashboard-content">
          <div key={location.pathname} className="dashboard-page-enter">
            {children}
          </div>
        </main>
      </div>
      </div>
      {exportingExcel && <ExportLoadingOverlay />}
    </div>
  );
};

export default DashboardLayout;
